import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../lib/api';

type SettingsTab = 'business' | 'wise' | 'items' | 'rates' | 'apiKeys';

interface BusinessInfo {
  company_name: string | null;
  company_name_secondary: string | null;
  registration_number: string | null;
  tax_id: string | null;
  tax_id_type: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  address_country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  bank_note: string | null;
}

interface BusinessInfoForm {
  company_name: string;
  company_name_secondary: string;
  registration_number: string;
  tax_id: string;
  tax_id_type: string;
  address_line1: string;
  address_line2: string;
  address_city: string;
  address_state: string;
  address_postal_code: string;
  address_country: string;
  phone: string;
  email: string;
  website: string;
  bank_note: string;
}

interface WiseBankAccount {
  id: string;
  currency: string;
  bank_name: string;
  account_holder: string;
  account_number: string;
  routing_number: string | null;
  swift_code: string | null;
  iban: string | null;
  bsb_code: string | null;
  sort_code: string | null;
  bank_address: string | null;
  notes: string | null;
}

interface WiseBankAccountForm {
  currency: string;
  bank_name: string;
  account_holder: string;
  account_number: string;
  routing_number: string;
  swift_code: string;
  iban: string;
  bsb_code: string;
  sort_code: string;
  bank_address: string;
  notes: string;
}

interface ItemTemplate {
  id: string;
  name: string;
  default_unit_price: number;
  currency: string | null;
  category: string | null;
  is_active: boolean;
}

interface ItemTemplateForm {
  name: string;
  default_unit_price: string;
  currency: string;
  category: string;
}

interface CostRateRow {
  id: string;
  provider: string;
  model: string;
  input_price_per_1m: number;
  output_price_per_1m: number;
  effective_from: string;
  effective_to: string | null;
  source_url: string | null;
  notes: string | null;
}

interface NewRateForm {
  provider: string;
  model: string;
  input_price_per_1m: string;
  output_price_per_1m: string;
  source_url: string;
  notes: string;
}

interface ProjectOption {
  id: string;
  name: string;
}

interface ApiKeyMapping {
  id: string;
  project_id: string;
  project_name: string;
  client_name: string;
  provider: string;
  key_identifier: string;
  label: string | null;
  is_active: boolean;
  created_at: string;
}

interface ApiKeyMappingForm {
  project_id: string;
  provider: string;
  key_identifier: string;
  label: string;
  is_active: boolean;
}

const defaultBusinessForm: BusinessInfoForm = {
  company_name: '',
  company_name_secondary: '',
  registration_number: '',
  tax_id: '',
  tax_id_type: 'hk_br',
  address_line1: '',
  address_line2: '',
  address_city: '',
  address_state: '',
  address_postal_code: '',
  address_country: '',
  phone: '',
  email: '',
  website: '',
  bank_note: '',
};

const defaultWiseForm: WiseBankAccountForm = {
  currency: 'USD',
  bank_name: '',
  account_holder: '',
  account_number: '',
  routing_number: '',
  swift_code: '',
  iban: '',
  bsb_code: '',
  sort_code: '',
  bank_address: '',
  notes: '',
};

const defaultItemTemplateForm: ItemTemplateForm = {
  name: '',
  default_unit_price: '',
  currency: '',
  category: '',
};

const defaultApiKeyMappingForm: ApiKeyMappingForm = {
  project_id: '',
  provider: 'openai',
  key_identifier: '',
  label: '',
  is_active: true,
};

export function SettingsPage() {
  const [searchParams] = useSearchParams();
  const initialTab = parseSettingsTab(searchParams.get('tab'));
  const initialProjectFilter = searchParams.get('project_id')?.trim() ?? '';

  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab ?? 'business');

  const [businessForm, setBusinessForm] = useState<BusinessInfoForm>(defaultBusinessForm);
  const [businessError, setBusinessError] = useState<string | null>(null);
  const [businessSaving, setBusinessSaving] = useState(false);

  const [wiseAccounts, setWiseAccounts] = useState<WiseBankAccount[]>([]);
  const [wiseError, setWiseError] = useState<string | null>(null);
  const [wiseForm, setWiseForm] = useState<WiseBankAccountForm>(defaultWiseForm);
  const [wiseSaving, setWiseSaving] = useState(false);
  const [showWiseForm, setShowWiseForm] = useState(false);
  const [editingWiseAccountId, setEditingWiseAccountId] = useState<string | null>(null);

  const [itemTemplates, setItemTemplates] = useState<ItemTemplate[]>([]);
  const [itemTemplateError, setItemTemplateError] = useState<string | null>(null);
  const [itemTemplateForm, setItemTemplateForm] =
    useState<ItemTemplateForm>(defaultItemTemplateForm);
  const [itemTemplateSaving, setItemTemplateSaving] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);

  const [rates, setRates] = useState<CostRateRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rateSaving, setRateSaving] = useState(false);
  const [deactivatingRateId, setDeactivatingRateId] = useState<string | null>(null);
  const [form, setForm] = useState<NewRateForm>({
    provider: 'openai',
    model: '',
    input_price_per_1m: '',
    output_price_per_1m: '',
    source_url: '',
    notes: '',
  });

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [apiKeyMappings, setApiKeyMappings] = useState<ApiKeyMapping[]>([]);
  const [apiKeyMappingForm, setApiKeyMappingForm] =
    useState<ApiKeyMappingForm>(defaultApiKeyMappingForm);
  const [apiKeyMappingError, setApiKeyMappingError] = useState<string | null>(null);
  const [apiKeyMappingSaving, setApiKeyMappingSaving] = useState(false);
  const [showApiKeyMappingForm, setShowApiKeyMappingForm] = useState(false);
  const [editingApiKeyMappingId, setEditingApiKeyMappingId] = useState<string | null>(null);
  const [apiKeyProjectFilter, setApiKeyProjectFilter] = useState(initialProjectFilter);

  useEffect(() => {
    void loadBusinessInfo();
    void loadWiseBankAccounts();
    void loadItemTemplates();
    void loadRates();
    void loadProjects();
    void loadApiKeyMappings(initialProjectFilter);
  }, []);

  useEffect(() => {
    const tab = parseSettingsTab(searchParams.get('tab'));
    if (tab) {
      setActiveTab(tab);
    }

    const projectId = searchParams.get('project_id')?.trim() ?? '';
    setApiKeyProjectFilter(projectId);
    if (tab === 'apiKeys' || projectId) {
      void loadApiKeyMappings(projectId);
    }
  }, [searchParams]);

  async function loadProjects(): Promise<void> {
    try {
      const data = await apiGet<ProjectOption[]>('/api/projects');
      setProjects(data);
      setApiKeyMappingError(null);
    } catch (err) {
      setApiKeyMappingError(err instanceof Error ? err.message : 'Failed to load projects');
    }
  }

  async function loadApiKeyMappings(projectIdFilter: string = apiKeyProjectFilter): Promise<void> {
    try {
      const query = projectIdFilter
        ? `?provider=openai&project_id=${encodeURIComponent(projectIdFilter)}`
        : '?provider=openai';
      const data = await apiGet<ApiKeyMapping[]>(`/api/settings/api-key-mappings${query}`);
      setApiKeyMappings(data);
      setApiKeyMappingError(null);
    } catch (err) {
      setApiKeyMappingError(err instanceof Error ? err.message : 'Failed to load API key mappings');
    }
  }

  function resetApiKeyMappingForm(): void {
    setApiKeyMappingForm(defaultApiKeyMappingForm);
    setEditingApiKeyMappingId(null);
    setShowApiKeyMappingForm(false);
  }

  function startEditApiKeyMapping(mapping: ApiKeyMapping): void {
    setApiKeyMappingForm({
      project_id: mapping.project_id,
      provider: mapping.provider,
      key_identifier: mapping.key_identifier,
      label: mapping.label ?? '',
      is_active: mapping.is_active,
    });
    setEditingApiKeyMappingId(mapping.id);
    setShowApiKeyMappingForm(true);
  }

  async function saveApiKeyMapping(): Promise<void> {
    if (!apiKeyMappingForm.project_id || !apiKeyMappingForm.key_identifier.trim()) {
      setApiKeyMappingError('Project and key identifier are required');
      return;
    }

    try {
      setApiKeyMappingSaving(true);
      if (editingApiKeyMappingId) {
        await apiPatch(`/api/settings/api-key-mappings/${editingApiKeyMappingId}`, {
          project_id: apiKeyMappingForm.project_id,
          key_identifier: apiKeyMappingForm.key_identifier.trim(),
          label: apiKeyMappingForm.label.trim() || null,
          is_active: apiKeyMappingForm.is_active,
        });
      } else {
        await apiPost('/api/settings/api-key-mappings', {
          project_id: apiKeyMappingForm.project_id,
          provider: apiKeyMappingForm.provider,
          key_identifier: apiKeyMappingForm.key_identifier.trim(),
          label: apiKeyMappingForm.label.trim() || null,
          is_active: apiKeyMappingForm.is_active,
        });
      }

      resetApiKeyMappingForm();
      setApiKeyMappingError(null);
      await loadApiKeyMappings();
    } catch (err) {
      setApiKeyMappingError(err instanceof Error ? err.message : 'Failed to save API key mapping');
    } finally {
      setApiKeyMappingSaving(false);
    }
  }

  async function deleteApiKeyMapping(id: string): Promise<void> {
    try {
      await apiDelete(`/api/settings/api-key-mappings/${id}`);
      setApiKeyMappingError(null);
      await loadApiKeyMappings();
    } catch (err) {
      setApiKeyMappingError(
        err instanceof Error ? err.message : 'Failed to delete API key mapping',
      );
    }
  }

  async function loadBusinessInfo(): Promise<void> {
    try {
      const data = await apiGet<BusinessInfo>('/api/settings/business');
      setBusinessForm({
        company_name: data.company_name ?? '',
        company_name_secondary: data.company_name_secondary ?? '',
        registration_number: data.registration_number ?? '',
        tax_id: data.tax_id ?? '',
        tax_id_type: data.tax_id_type ?? 'hk_br',
        address_line1: data.address_line1 ?? '',
        address_line2: data.address_line2 ?? '',
        address_city: data.address_city ?? '',
        address_state: data.address_state ?? '',
        address_postal_code: data.address_postal_code ?? '',
        address_country: data.address_country ?? '',
        phone: data.phone ?? '',
        email: data.email ?? '',
        website: data.website ?? '',
        bank_note: data.bank_note ?? '',
      });
      setBusinessError(null);
    } catch (err) {
      setBusinessError(err instanceof Error ? err.message : 'Failed to load business info');
    }
  }

  async function saveBusinessInfo(): Promise<void> {
    if (!businessForm.company_name.trim()) {
      setBusinessError('Company name is required');
      return;
    }

    try {
      setBusinessSaving(true);
      await apiPut('/api/settings/business', {
        ...businessForm,
        company_name: businessForm.company_name.trim(),
      });
      setBusinessError(null);
      await loadBusinessInfo();
    } catch (err) {
      setBusinessError(err instanceof Error ? err.message : 'Failed to save business info');
    } finally {
      setBusinessSaving(false);
    }
  }

  async function loadWiseBankAccounts(): Promise<void> {
    try {
      const data = await apiGet<WiseBankAccount[]>('/api/settings/wise-bank-accounts');
      setWiseAccounts(data);
      setWiseError(null);
    } catch (err) {
      setWiseError(err instanceof Error ? err.message : 'Failed to load bank accounts');
    }
  }

  function resetWiseForm(): void {
    setWiseForm(defaultWiseForm);
    setEditingWiseAccountId(null);
    setShowWiseForm(false);
  }

  async function saveWiseBankAccount(): Promise<void> {
    if (!wiseForm.bank_name || !wiseForm.account_holder || !wiseForm.account_number) {
      setWiseError('Currency, bank name, account holder, and account number are required');
      return;
    }

    const payload = {
      ...wiseForm,
      bank_name: wiseForm.bank_name.trim(),
      account_holder: wiseForm.account_holder.trim(),
      account_number: wiseForm.account_number.trim(),
    };

    try {
      setWiseSaving(true);
      if (editingWiseAccountId) {
        await apiPut(`/api/settings/wise-bank-accounts/${editingWiseAccountId}`, payload);
      } else {
        await apiPost('/api/settings/wise-bank-accounts', payload);
      }
      setWiseError(null);
      resetWiseForm();
      await loadWiseBankAccounts();
    } catch (err) {
      setWiseError(err instanceof Error ? err.message : 'Failed to save bank account');
    } finally {
      setWiseSaving(false);
    }
  }

  function startEditWiseBankAccount(account: WiseBankAccount): void {
    setWiseForm({
      currency: account.currency,
      bank_name: account.bank_name,
      account_holder: account.account_holder,
      account_number: account.account_number,
      routing_number: account.routing_number ?? '',
      swift_code: account.swift_code ?? '',
      iban: account.iban ?? '',
      bsb_code: account.bsb_code ?? '',
      sort_code: account.sort_code ?? '',
      bank_address: account.bank_address ?? '',
      notes: account.notes ?? '',
    });
    setEditingWiseAccountId(account.id);
    setShowWiseForm(true);
  }

  async function deleteWiseBankAccount(accountId: string): Promise<void> {
    try {
      await apiDelete(`/api/settings/wise-bank-accounts/${accountId}`);
      await loadWiseBankAccounts();
      setWiseError(null);
    } catch (err) {
      setWiseError(err instanceof Error ? err.message : 'Failed to delete bank account');
    }
  }

  async function loadItemTemplates(): Promise<void> {
    try {
      const data = await apiGet<ItemTemplate[]>('/api/settings/item-templates');
      setItemTemplates(data);
      setItemTemplateError(null);
    } catch (err) {
      setItemTemplateError(err instanceof Error ? err.message : 'Failed to load item templates');
    }
  }

  function resetItemTemplateForm(): void {
    setItemTemplateForm(defaultItemTemplateForm);
    setShowItemForm(false);
  }

  async function createItemTemplate(): Promise<void> {
    const price = Number(itemTemplateForm.default_unit_price);
    if (!itemTemplateForm.name.trim() || Number.isNaN(price)) {
      setItemTemplateError('Name and default unit price are required');
      return;
    }

    try {
      setItemTemplateSaving(true);
      await apiPost('/api/settings/item-templates', {
        name: itemTemplateForm.name.trim(),
        default_unit_price: price,
        currency: itemTemplateForm.currency || null,
        category: itemTemplateForm.category || null,
      });
      setItemTemplateError(null);
      resetItemTemplateForm();
      await loadItemTemplates();
    } catch (err) {
      setItemTemplateError(err instanceof Error ? err.message : 'Failed to create item template');
    } finally {
      setItemTemplateSaving(false);
    }
  }

  async function toggleItemTemplate(template: ItemTemplate): Promise<void> {
    try {
      await apiPatch(`/api/settings/item-templates/${template.id}`, {
        is_active: !template.is_active,
      });
      await loadItemTemplates();
      setItemTemplateError(null);
    } catch (err) {
      setItemTemplateError(err instanceof Error ? err.message : 'Failed to update item template');
    }
  }

  async function deleteItemTemplate(templateId: string): Promise<void> {
    try {
      await apiDelete(`/api/settings/item-templates/${templateId}`);
      await loadItemTemplates();
      setItemTemplateError(null);
    } catch (err) {
      setItemTemplateError(err instanceof Error ? err.message : 'Failed to delete item template');
    }
  }

  async function loadRates(): Promise<void> {
    try {
      const data = await apiGet<CostRateRow[]>('/api/settings/rates');
      setRates(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rates');
    }
  }

  async function createRate(): Promise<void> {
    const input = Number(form.input_price_per_1m);
    const output = Number(form.output_price_per_1m);
    if (!form.model || Number.isNaN(input) || Number.isNaN(output)) {
      setError('Provider, model, input price, and output price are required');
      return;
    }

    setRateSaving(true);
    try {
      await apiPost('/api/settings/rates', {
        provider: form.provider,
        model: form.model,
        input_price_per_1m: input,
        output_price_per_1m: output,
        source_url: form.source_url || undefined,
        notes: form.notes || undefined,
      });

      setForm({
        provider: form.provider,
        model: '',
        input_price_per_1m: '',
        output_price_per_1m: '',
        source_url: '',
        notes: '',
      });
      await loadRates();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add rate');
    } finally {
      setRateSaving(false);
    }
  }

  async function deactivateRate(rateId: string): Promise<void> {
    setDeactivatingRateId(rateId);
    try {
      await apiPatch(`/api/settings/rates/${rateId}`, {
        effective_to: new Date().toISOString(),
      });
      await loadRates();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate rate');
    } finally {
      setDeactivatingRateId(null);
    }
  }

  return (
    <div>
      <h1 className="text-xl font-semibold">Settings</h1>
      <p className="mt-1 text-sm text-slate-600">
        Manage business profile, payment rails, item defaults, and cost rates.
      </p>

      <div className="mt-6 border-b border-slate-200">
        <nav className="-mb-px flex gap-6" aria-label="Settings tabs">
          <button
            type="button"
            className={`border-b-2 px-1 pb-3 text-sm font-medium ${
              activeTab === 'business'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
            onClick={() => {
              setActiveTab('business');
            }}
          >
            Business Info
          </button>
          <button
            type="button"
            className={`border-b-2 px-1 pb-3 text-sm font-medium ${
              activeTab === 'wise'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
            onClick={() => {
              setActiveTab('wise');
            }}
          >
            Wise Bank Accounts
          </button>
          <button
            type="button"
            className={`border-b-2 px-1 pb-3 text-sm font-medium ${
              activeTab === 'items'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
            onClick={() => {
              setActiveTab('items');
            }}
          >
            Item Templates
          </button>
          <button
            type="button"
            className={`border-b-2 px-1 pb-3 text-sm font-medium ${
              activeTab === 'rates'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
            onClick={() => {
              setActiveTab('rates');
            }}
          >
            Cost Rates
          </button>
          <button
            type="button"
            className={`border-b-2 px-1 pb-3 text-sm font-medium ${
              activeTab === 'apiKeys'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
            onClick={() => {
              setActiveTab('apiKeys');
            }}
          >
            API Key Mappings
          </button>
        </nav>
      </div>

      {activeTab === 'business' ? (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h2 className="text-sm font-semibold text-slate-800">Business info</h2>
          <p className="mt-1 text-sm text-slate-600">
            This information is used on invoices and payment instructions.
          </p>
          {businessError ? <p className="mt-3 text-sm text-red-600">{businessError}</p> : null}
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Company Name</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.company_name}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, company_name: event.target.value }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Company Name Secondary</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.company_name_secondary}
                onChange={(event) =>
                  setBusinessForm((prev) => ({
                    ...prev,
                    company_name_secondary: event.target.value,
                  }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Registration Number</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.registration_number}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, registration_number: event.target.value }))
                }
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Tax ID</span>
                <input
                  className="w-full rounded-md border border-slate-300 px-2 py-1"
                  value={businessForm.tax_id}
                  onChange={(event) =>
                    setBusinessForm((prev) => ({ ...prev, tax_id: event.target.value }))
                  }
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Tax ID Type</span>
                <select
                  className="w-full rounded-md border border-slate-300 px-2 py-1"
                  value={businessForm.tax_id_type}
                  onChange={(event) =>
                    setBusinessForm((prev) => ({ ...prev, tax_id_type: event.target.value }))
                  }
                >
                  <option value="hk_br">hk_br</option>
                  <option value="us_ein">us_ein</option>
                  <option value="au_abn">au_abn</option>
                  <option value="cn_tin">cn_tin</option>
                  <option value="eu_vat">eu_vat</option>
                  <option value="gb_vat">gb_vat</option>
                  <option value="sg_uen">sg_uen</option>
                  <option value="other">other</option>
                </select>
              </label>
            </div>
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-slate-600">Address Line 1</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.address_line1}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, address_line1: event.target.value }))
                }
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-slate-600">Address Line 2</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.address_line2}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, address_line2: event.target.value }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">City</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.address_city}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, address_city: event.target.value }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">State</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.address_state}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, address_state: event.target.value }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Postal Code</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.address_postal_code}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, address_postal_code: event.target.value }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Country (2-letter ISO)</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1 uppercase"
                maxLength={2}
                value={businessForm.address_country}
                onChange={(event) =>
                  setBusinessForm((prev) => ({
                    ...prev,
                    address_country: event.target.value.toUpperCase(),
                  }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Phone</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.phone}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, phone: event.target.value }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Email</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                type="email"
                value={businessForm.email}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, email: event.target.value }))
                }
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Website</span>
              <input
                className="w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.website}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, website: event.target.value }))
                }
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-slate-600">Bank Note</span>
              <textarea
                className="h-24 w-full rounded-md border border-slate-300 px-2 py-1"
                value={businessForm.bank_note}
                onChange={(event) =>
                  setBusinessForm((prev) => ({ ...prev, bank_note: event.target.value }))
                }
              />
            </label>
          </div>
          <button
            type="button"
            className="mt-4 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={businessSaving}
            onClick={() => {
              void saveBusinessInfo();
            }}
          >
            Save
          </button>
        </div>
      ) : null}

      {activeTab === 'wise' ? (
        <div className="mt-4 space-y-4">
          {wiseError ? <p className="text-sm text-red-600">{wiseError}</p> : null}
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Wise bank accounts</h2>
            <button
              type="button"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
              onClick={() => {
                setEditingWiseAccountId(null);
                setWiseForm(defaultWiseForm);
                setShowWiseForm(true);
              }}
            >
              Add Bank Account
            </button>
          </div>

          {showWiseForm ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-800">
                {editingWiseAccountId ? 'Edit bank account' : 'Add bank account'}
              </h3>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Currency</span>
                  <select
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.currency}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, currency: event.target.value }))
                    }
                  >
                    <option value="USD">USD</option>
                    <option value="HKD">HKD</option>
                    <option value="AUD">AUD</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                    <option value="CNY">CNY</option>
                    <option value="SGD">SGD</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Bank Name</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.bank_name}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, bank_name: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Account Holder</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.account_holder}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, account_holder: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Account Number</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.account_number}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, account_number: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Routing Number</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.routing_number}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, routing_number: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">SWIFT/BIC Code</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.swift_code}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, swift_code: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">IBAN</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.iban}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, iban: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">BSB Code</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.bsb_code}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, bsb_code: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Sort Code</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.sort_code}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, sort_code: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm md:col-span-2">
                  <span className="mb-1 block text-slate-600">Bank Address</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.bank_address}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, bank_address: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm md:col-span-2">
                  <span className="mb-1 block text-slate-600">Notes</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={wiseForm.notes}
                    onChange={(event) =>
                      setWiseForm((prev) => ({ ...prev, notes: event.target.value }))
                    }
                  />
                </label>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={wiseSaving}
                  onClick={() => {
                    void saveWiseBankAccount();
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  onClick={() => {
                    resetWiseForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="border-b border-slate-200 px-3 py-2">Currency</th>
                  <th className="border-b border-slate-200 px-3 py-2">Bank Name</th>
                  <th className="border-b border-slate-200 px-3 py-2">Account Holder</th>
                  <th className="border-b border-slate-200 px-3 py-2">Account Number</th>
                  <th className="border-b border-slate-200 px-3 py-2">SWIFT</th>
                  <th className="border-b border-slate-200 px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {wiseAccounts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-slate-500">
                      No bank accounts configured.
                    </td>
                  </tr>
                ) : (
                  wiseAccounts.map((account) => (
                    <tr key={account.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-3 py-2">{account.currency}</td>
                      <td className="px-3 py-2">{account.bank_name}</td>
                      <td className="px-3 py-2">{account.account_holder}</td>
                      <td className="px-3 py-2">{account.account_number}</td>
                      <td className="px-3 py-2">{account.swift_code || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => {
                              startEditWiseBankAccount(account);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => {
                              void deleteWiseBankAccount(account.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {activeTab === 'items' ? (
        <div className="mt-4 space-y-4">
          {itemTemplateError ? <p className="text-sm text-red-600">{itemTemplateError}</p> : null}
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Item templates</h2>
            <button
              type="button"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
              onClick={() => {
                setShowItemForm(true);
              }}
            >
              Add Item
            </button>
          </div>

          {showItemForm ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-800">Add item template</h3>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Name</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={itemTemplateForm.name}
                    onChange={(event) =>
                      setItemTemplateForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Default Unit Price</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    type="number"
                    step="0.0001"
                    value={itemTemplateForm.default_unit_price}
                    onChange={(event) =>
                      setItemTemplateForm((prev) => ({
                        ...prev,
                        default_unit_price: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Currency</span>
                  <select
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={itemTemplateForm.currency}
                    onChange={(event) =>
                      setItemTemplateForm((prev) => ({ ...prev, currency: event.target.value }))
                    }
                  >
                    <option value="">Any</option>
                    <option value="USD">USD</option>
                    <option value="HKD">HKD</option>
                    <option value="AUD">AUD</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Category</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={itemTemplateForm.category}
                    onChange={(event) =>
                      setItemTemplateForm((prev) => ({ ...prev, category: event.target.value }))
                    }
                  />
                </label>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={itemTemplateSaving}
                  onClick={() => {
                    void createItemTemplate();
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  onClick={() => {
                    resetItemTemplateForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="border-b border-slate-200 px-3 py-2">Name</th>
                  <th className="border-b border-slate-200 px-3 py-2">Default Price</th>
                  <th className="border-b border-slate-200 px-3 py-2">Currency</th>
                  <th className="border-b border-slate-200 px-3 py-2">Category</th>
                  <th className="border-b border-slate-200 px-3 py-2">Active</th>
                  <th className="border-b border-slate-200 px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {itemTemplates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-slate-500">
                      No item templates configured.
                    </td>
                  </tr>
                ) : (
                  itemTemplates.map((template) => (
                    <tr key={template.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-3 py-2">{template.name}</td>
                      <td className="px-3 py-2">{template.default_unit_price}</td>
                      <td className="px-3 py-2">{template.currency || 'Any'}</td>
                      <td className="px-3 py-2">{template.category || '-'}</td>
                      <td className="px-3 py-2">{template.is_active ? 'Yes' : 'No'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => {
                              void toggleItemTemplate(template);
                            }}
                          >
                            {template.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => {
                              void deleteItemTemplate(template.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {activeTab === 'rates' ? (
        <>
          {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h2 className="text-sm font-semibold text-slate-800">Add rate</h2>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Provider</span>
                <select
                  className="w-full rounded-md border border-slate-300 px-2 py-1"
                  value={form.provider}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, provider: event.target.value }))
                  }
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="cloudflare">Cloudflare</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Model</span>
                <input
                  className="w-full rounded-md border border-slate-300 px-2 py-1"
                  value={form.model}
                  onChange={(event) => setForm((prev) => ({ ...prev, model: event.target.value }))}
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Input per 1M (USD)</span>
                <input
                  className="w-full rounded-md border border-slate-300 px-2 py-1"
                  type="number"
                  step="0.0001"
                  value={form.input_price_per_1m}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, input_price_per_1m: event.target.value }))
                  }
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Output per 1M (USD)</span>
                <input
                  className="w-full rounded-md border border-slate-300 px-2 py-1"
                  type="number"
                  step="0.0001"
                  value={form.output_price_per_1m}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, output_price_per_1m: event.target.value }))
                  }
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Source URL</span>
                <input
                  className="w-full rounded-md border border-slate-300 px-2 py-1"
                  value={form.source_url}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, source_url: event.target.value }))
                  }
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Notes</span>
                <input
                  className="w-full rounded-md border border-slate-300 px-2 py-1"
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                />
              </label>
            </div>
            <button
              type="button"
              className="mt-3 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={rateSaving}
              onClick={() => {
                void createRate();
              }}
            >
              {rateSaving ? 'Adding...' : 'Add rate'}
            </button>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-3">Provider</th>
                  <th className="py-2 pr-3">Model</th>
                  <th className="py-2 pr-3">Input</th>
                  <th className="py-2 pr-3">Output</th>
                  <th className="py-2 pr-3">Effective from</th>
                  <th className="py-2 pr-3">Effective to</th>
                  <th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((rate) => (
                  <tr key={rate.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3">{rate.provider}</td>
                    <td className="py-2 pr-3">{rate.model}</td>
                    <td className="py-2 pr-3">{rate.input_price_per_1m}</td>
                    <td className="py-2 pr-3">{rate.output_price_per_1m}</td>
                    <td className="py-2 pr-3">{formatDate(rate.effective_from)}</td>
                    <td className="py-2 pr-3">
                      {rate.effective_to ? formatDate(rate.effective_to) : 'Active'}
                    </td>
                    <td className="py-2">
                      {rate.effective_to ? (
                        <span className="text-slate-400">-</span>
                      ) : (
                        <button
                          type="button"
                          className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={deactivatingRateId !== null}
                          onClick={() => {
                            void deactivateRate(rate.id);
                          }}
                        >
                          {deactivatingRateId === rate.id ? 'Deactivating...' : 'Deactivate'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {activeTab === 'apiKeys' ? (
        <div className="mt-4 space-y-4">
          {apiKeyMappingError ? <p className="text-sm text-red-600">{apiKeyMappingError}</p> : null}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">
                OpenAI key-to-project mappings
              </h2>
              <p className="mt-1 text-xs text-slate-600">
                Use OpenAI <code>api_key_id</code> or OpenAI <code>project_id</code> as key
                identifier.
              </p>
            </div>
            <button
              type="button"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
              onClick={() => {
                setEditingApiKeyMappingId(null);
                setApiKeyMappingForm({
                  ...defaultApiKeyMappingForm,
                  project_id: apiKeyProjectFilter || '',
                });
                setShowApiKeyMappingForm(true);
              }}
            >
              Add Mapping
            </button>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">Project filter</label>
            <select
              className="rounded-md border border-slate-300 px-2 py-1 text-sm"
              value={apiKeyProjectFilter}
              onChange={(event) => {
                const next = event.target.value;
                setApiKeyProjectFilter(next);
                void loadApiKeyMappings(next);
              }}
            >
              <option value="">All projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.id})
                </option>
              ))}
            </select>
          </div>

          {showApiKeyMappingForm ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-800">
                {editingApiKeyMappingId ? 'Edit mapping' : 'Add mapping'}
              </h3>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Project</span>
                  <select
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={apiKeyMappingForm.project_id}
                    onChange={(event) =>
                      setApiKeyMappingForm((prev) => ({ ...prev, project_id: event.target.value }))
                    }
                  >
                    <option value="">Select project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name} ({project.id})
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-sm">
                  <span className="mb-1 block text-slate-600">Provider</span>
                  <select
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={apiKeyMappingForm.provider}
                    onChange={(event) =>
                      setApiKeyMappingForm((prev) => ({ ...prev, provider: event.target.value }))
                    }
                    disabled={editingApiKeyMappingId !== null}
                  >
                    <option value="openai">openai</option>
                  </select>
                </label>

                <label className="text-sm md:col-span-2">
                  <span className="mb-1 block text-slate-600">Key Identifier</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={apiKeyMappingForm.key_identifier}
                    onChange={(event) =>
                      setApiKeyMappingForm((prev) => ({
                        ...prev,
                        key_identifier: event.target.value,
                      }))
                    }
                    placeholder="api_key_id (key_xxx) or project_id (proj_xxx from OpenAI)"
                  />
                </label>

                <label className="text-sm md:col-span-2">
                  <span className="mb-1 block text-slate-600">Label</span>
                  <input
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={apiKeyMappingForm.label}
                    onChange={(event) =>
                      setApiKeyMappingForm((prev) => ({ ...prev, label: event.target.value }))
                    }
                    placeholder="Optional: prod-key-1 / team-alpha"
                  />
                </label>

                <label className="text-sm md:col-span-2">
                  <span className="mb-1 block text-slate-600">Status</span>
                  <select
                    className="w-full rounded-md border border-slate-300 px-2 py-1"
                    value={apiKeyMappingForm.is_active ? 'active' : 'inactive'}
                    onChange={(event) =>
                      setApiKeyMappingForm((prev) => ({
                        ...prev,
                        is_active: event.target.value === 'active',
                      }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={apiKeyMappingSaving}
                  onClick={() => {
                    void saveApiKeyMapping();
                  }}
                >
                  {apiKeyMappingSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  onClick={() => {
                    resetApiKeyMappingForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="border-b border-slate-200 px-3 py-2">Project</th>
                  <th className="border-b border-slate-200 px-3 py-2">Client</th>
                  <th className="border-b border-slate-200 px-3 py-2">Provider</th>
                  <th className="border-b border-slate-200 px-3 py-2">Key Identifier</th>
                  <th className="border-b border-slate-200 px-3 py-2">Label</th>
                  <th className="border-b border-slate-200 px-3 py-2">Status</th>
                  <th className="border-b border-slate-200 px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeyMappings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-center text-slate-500">
                      No API key mappings configured.
                    </td>
                  </tr>
                ) : (
                  apiKeyMappings.map((mapping) => (
                    <tr key={mapping.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-3 py-2">{mapping.project_name}</td>
                      <td className="px-3 py-2">{mapping.client_name}</td>
                      <td className="px-3 py-2">{mapping.provider}</td>
                      <td className="px-3 py-2 font-mono text-xs">{mapping.key_identifier}</td>
                      <td className="px-3 py-2">{mapping.label || '-'}</td>
                      <td className="px-3 py-2">
                        {mapping.is_active ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            active
                          </span>
                        ) : (
                          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                            inactive
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => {
                              startEditApiKeyMapping(mapping);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => {
                              void deleteApiKeyMapping(mapping.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

function parseSettingsTab(value: string | null): SettingsTab | null {
  if (!value) {
    return null;
  }

  if (
    value === 'business' ||
    value === 'wise' ||
    value === 'items' ||
    value === 'rates' ||
    value === 'apiKeys'
  ) {
    return value;
  }

  return null;
}
